import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Ban, Clock, RotateCcw } from "lucide-react";
import { S, MANROPE, FOCUS, API, auDate } from "./lib";

// Who we are not emailing, and why. The standing record behind every draft that
// does not get written.
export default function StoppedList({ refreshKey, onChange }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/stopped`);
      setRows(data.stopped || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the do-not-email list");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

  const resume = async (row) => {
    setBusy(row.id);
    try {
      await axios.post(`${API}/prospects/${row.id}/resume`, { reason: "Allowed again on screen" });
      toast.success(`${row.company_name} can be emailed again`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not lift that");
    } finally { setBusy(""); }
  };

  if (!rows.length) return null;

  return (
    <div className="rounded-[11px] overflow-hidden" data-testid="outreach-stopped"
      style={{ background: S.card, border: `1px solid ${S.border}` }}>
      <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: `1px solid ${S.divider}` }}>
        <Ban className="w-3.5 h-3.5 pointer-events-none" style={{ color: S.bad }} />
        <div className="text-[13px] font-bold pointer-events-none" style={{ ...MANROPE, color: S.ink }}>
          Not to be emailed
        </div>
        <span className="mono text-[11px] pointer-events-none" style={{ color: S.ink3 }}>{rows.length}</span>
      </div>
      <div className="max-h-[300px] overflow-y-auto">
        {rows.map((r) => (
          <div key={r.id} data-testid={`outreach-stopped-${r.id}`}
            className="px-4 py-2.5 flex items-start gap-3" style={{ borderBottom: `1px solid ${S.divider}` }}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[12.5px] font-semibold truncate" style={{ color: S.ink }}>
                  {r.company_name || "—"}
                </span>
                {r.kind === "paused" ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase"
                    style={{ color: S.accentInk }}>
                    <Clock className="w-3 h-3" /> until {auDate(r.until)}
                  </span>
                ) : (
                  <span className="text-[10px] font-bold uppercase" style={{ color: S.bad }}>
                    Do not email
                  </span>
                )}
              </div>
              <div className="text-[11px]" style={{ color: S.ink2 }}>
                “{r.reason || "No reason recorded"}”{r.by ? ` — ${r.by}` : ""}
              </div>
            </div>
            <button onClick={() => resume(r)} disabled={busy === r.id}
              data-testid={`outreach-allow-${r.id}`} title="Allow outreach again"
              className={`h-8 px-2 rounded-[6px] inline-flex items-center gap-1 text-[10.5px] font-bold uppercase disabled:opacity-50 ${FOCUS}`}
              style={{ background: S.panel, border: `1px solid ${S.border}`, color: S.ink2 }}>
              <RotateCcw className="w-3 h-3" /> Allow
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
